import { LinearProgress, Stack, Typography, Divider, linearProgressClasses } from "@mui/material";
import { ReactComponent as ProfileCalendar } from '../../../assests/skillCalendar.svg';
import LocationSvg from "../../../assests/location";
import GroupsIcon from "@mui/icons-material/Groups";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BusinessIcon from "@mui/icons-material/Business";
import { useEffect, useState } from "react";
import EllipsisTooltip from "../../tooltip/EllipsisTooltip";
import EllipsisChip from "../../tooltip/EllipsisChip";
import { ReactComponent as ProjectFile } from '../../../assests/file.svg';
import UtilizationProgress from "../../progressbar/UtilizationProgress";
import { BadgeCheck, Building2, Calendar, CalendarDays, Check, Edit2, ExternalLink, FileText, InfoIcon, Loader, MapPin, MessageSquare, MessageSquareText, User, X } from "lucide-react";
import StatusBadge from "@component/statusbadgestailwind/StatusBadge";
import styled from "@emotion/styled";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import EllipsisTooltipCount from "@/components/ui/ellipsis-tooltip-count";
import Chip from "@component/Chip/Chip";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { updateAllocationStatus } from "@hooks/useUpdateAllocationStatus";
import { toast } from "@/hooks/use-toast";
import { actionNameConfig, SocketIdType } from "@configure/menuAndSubmenuConfig";
import { Textarea } from "@/components/ui/textarea";
import { LoaderComponent } from "@auth/PrivateRoute";
import { useParams } from "react-router-dom";

const ProfileCard = ({ children, onclick, className }) => (
  <div
    className={cn(`
    w-full 
    p-4 
    flex 
    flex-col 
    gap-3 
    overflow-hidden 
    box-border 
    transition-shadow 
    duration-300 
    ease-in-out 
    hover:shadow-md 
    shadow-sm 
    border 
    border-card-border 
    bg-card 
    rounded-lg 
    text-card-foreground 
    font-sans
    ${onclick ? "cursor-pointer" : "cursor-default"}
  `, className)}
    onClick={onclick}
  >
    {children}
  </div>
);
const getInitials = (name) => {
  if (!name) return "N/A";
  const words = name.trim().split(" ");
  if (words.length === 1) return words[0][0].toUpperCase();
  return (
    (words[0][0] || "") + (words[words.length - 1][0] || "")
  ).toUpperCase();
};
/////////////////////////////////////////////
// --------- Profile Card Section -------- //
/////////////////////////////////////////////

ProfileCard.Header = ({ avatar, name, role, status, color, bg }) => (
  <div className="font-sans flex flex-row justify-between items-start">
    {/* Left section: avatar + name/role */}
    <div className="flex flex-row items-center max-w-[70%]">
      <Avatar className="h-10 w-10 border-2 border-primary/20 mr-2">
        {avatar && avatar.trim() !== "" ? (
          <img
            src={avatar}
            alt="Profile"
            className="absolute inset-0 w-full h-full object-cover"
          />) : (
          <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
            {getInitials(name)}
          </AvatarFallback>
        )}
      </Avatar>
      <div className="min-w-0">
        <EllipsisTooltip className="text-foreground font-semibold text-md truncate">
          <span>
            {name}</span>
        </EllipsisTooltip>
        <EllipsisTooltip className="text-foreground/40 text-xs truncate">
          <span>{role}</span>
        </EllipsisTooltip>
      </div>
    </div>

    {/* Right section: status dot + text */}
    <div
      className="flex flex-row items-center space-x-1 min-w-0 rounded-full px-2 ml-3"
      style={{ backgroundColor: bg }}
    >
      <EllipsisTooltip className="text-[12px] font-medium truncate" style={{ color: color }}>
        <span>{status}</span>
      </EllipsisTooltip>
    </div>

  </div>
);


ProfileCard.Info = ({ location, department, band, rollOff, color }) => {
  return (
    <div className="flex flex-col text-sm text-foreground space-y-3">

      {/* Row 1: Location + Roll-off */}
      <div className="flex justify-between gap-x-6 gap-y-2">

        {/* Location */}
        <div className="flex items-center gap-2 text-xs w-[45%]">
          <MapPin className="w-4 h-4 shrink-0" />
          <EllipsisTooltip className="max-w-[40%]">
          <span className="text-muted-foreground font-medium whitespace-nowrap">
            Location:
          </span>
          </EllipsisTooltip>
          <EllipsisTooltip className="max-w-[100%] sm:max-w-[80%]">
            <span className="truncate whitespace-nowrap overflow-hidden text-foreground font-normal">
              {location || "-"}
            </span>
          </EllipsisTooltip>
        </div>

        {/* Roll-off */}
        {rollOff && (
          <div className="flex items-center gap-2 text-xs flex-shrink-0 w-[45%]">
            <CalendarDays className="w-4 h-4 shrink-0 text-muted-foreground" />
          <EllipsisTooltip className="max-w-[50%]">
            <span className="text-muted-foreground font-medium whitespace-nowrap">
              Roll-off:
            </span>
            </EllipsisTooltip>
            <EllipsisTooltip className="max-w-[50%]">
              <span className="truncate whitespace-nowrap overflow-hidden text-foreground font-normal">
                {rollOff}
              </span>
            </EllipsisTooltip>
          </div>
        )}
      </div>

      {/* Row 2: Department + Band */}
      <div className="flex justify-between gap-x-6 gap-y-2">

        {/* Department */}
        <div className="flex items-center gap-2 text-xs w-[45%]">
          <Building2 className="w-4 h-4 shrink-0 text-muted-foreground" />
          <EllipsisTooltip className="max-w-[50%]">
          <span className="text-muted-foreground font-medium whitespace-nowrap">
            Department:
          </span>
          </EllipsisTooltip>
          <EllipsisTooltip className="max-w-[40%]">
            <span className="truncate whitespace-nowrap overflow-hidden text-foreground font-normal">
              {department || "-"}
            </span>
          </EllipsisTooltip>
        </div>

        {/* Band */}
        <div className="flex items-center gap-2 text-xs flex-shrink-0 w-[45%]">
          <BadgeCheck className="w-4 h-4 shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground font-medium whitespace-nowrap">
            Band:
          </span>
          <EllipsisTooltip className="max-w-[80%]">
            <span className="truncate whitespace-nowrap overflow-hidden text-foreground font-normal">
              {band || "-"}
            </span>
          </EllipsisTooltip>
        </div>
      </div>

    </div>
  );
};

ProfileCard.Utilization = ({ value, color, name }) => (
  <>
    <UtilizationProgress value={value} color={color} label={name} />
  </>
);

const CurrentProject = ({ project, onEdit, employeeId, showIcon = false, getPeopleData, benchNotes, notesLoader, isAuthenticated }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(benchNotes || ''); // saved value  
  const [draftValue, setDraftValue] = useState(inputValue); // editable draft
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {currentTab} = useParams();
  
  
  useEffect(() => {
    // When benchNotes changes from parent, update the local state
    setInputValue(benchNotes || '');
    setDraftValue(benchNotes || '');
  }, [benchNotes]);

  const displayProjects =
    project.length > 0
      ? project.map((p) => p.project_name).join(', ')
      : 'N/A';
  // Use draftValue when editing, otherwise inputValue
  const displayValue = isEditing ? draftValue : inputValue;
  const hasValue = displayValue.trim() !== '';
  const colorClass = hasValue ? "text-green-600" : "text-gray-400";

  const handleSave = async () => {
    const trimmedValue = draftValue.trim();

    // ✅ Validation: don't allow empty or "N/A"
    if (!trimmedValue || trimmedValue.toLowerCase() === "n/a") {
      toast({
        title: "Validation Error",
        description: "Please enter valid message before saving.",
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);
    const payload = {
      type: "update_bench_notes",
      benchNotes: draftValue,
      employeeId: employeeId
    };
    const header = {
      "Socket-Action": actionNameConfig.RM.PEOPLE.PROFILE.BENCH_NOTES_UPDATION,
    };

    try {
      console.log("Sending payload:", payload);

      const response = await updateAllocationStatus(header, payload); // API call (use await)

      if (response) {
        getPeopleData(currentTab, "notes", employeeId) // Refresh data after successful update
        toast({
          title: "Success",
          description: "Message updated successfully!",
        });
        setInputValue(draftValue);
        setIsEditing(false);
        if (onEdit) onEdit(draftValue);
      } else {
        toast({
          title: "Error",
          description: "Failed to update Message.",
          variant: "destructive",
        });
      }

    } catch (error) {
      console.error("API Error:", error);
      toast({
        title: "Error",
        description: "Something went wrong while saving.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };


  const handleCancel = () => {
    setDraftValue(inputValue);
    setIsEditing(false);
  };

  const openDialog = () => {
    setIsPopoverOpen(true);
  }


  return (
    <div className="relative font-sans flex items-center gap-1 mt-1 min-w-0 rounded shadow-card p-1">
      {/* Project label */}
      <span className="text-[12px] font-bold text-foreground">Current:</span>

      <EllipsisTooltip className="max-w-[50%] flex-shrink">
        <span className="text-[12px] truncate max-w-[40%]">
          {displayProjects || "N/A"}
        </span>
      </EllipsisTooltip>

      {(isLoading || notesLoader) && (
        <div className="ml-auto">
          <LoaderComponent showText={false} height="2vh" />
        </div>
      )}


      {!isLoading && !notesLoader && showIcon && (
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <div
              role="button"
              tabIndex={0}
              aria-haspopup="dialog"
              aria-controls="notes-dialog"
              onClick={e =>{ e.stopPropagation(); openDialog()}}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') openDialog();
              }}
              onMouseEnter={openDialog}
              onMouseLeave={() => {
                if (!isEditing) {
                  setIsPopoverOpen(false);
                  setDraftValue(inputValue);
                }
              }}
              className="p-1 rounded hover:bg-gray-100 transition-colors cursor-pointer ml-auto"
            >
              <MessageSquareText className={`w-4 h-4 ${colorClass}`} />
            </div>
          </PopoverTrigger>

          <PopoverContent
            className="p-3 w-96 flex flex-col gap-2"
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => setIsPopoverOpen(true)}
            onMouseLeave={() => {
              if (!isEditing) setIsPopoverOpen(false);
            }}
          >
            <label className="text-[12px] font-bold text-foreground">
              Message
            </label>

            <Textarea
              rows={8}
              placeholder="N/A"
              className="text-[12px] border border-card-border w-full rounded px-1 py-0.5 resize-none 
          focus-visible:outline-none focus-visible:ring-0 focus-visible:border-card-border"
              value={draftValue}
              readOnly={!isEditing || !isAuthenticated}
              onChange={(e) => setDraftValue(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />

            <div className="flex justify-end gap-2 mt-1">
              {isEditing ? (
                <>
                  <button
                    className="text-green-600 hover:text-green-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSave();
                      setIsEditing(false);
                      setIsPopoverOpen(false);
                    }}
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    className="text-red-400 hover:text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancel();
                      setIsEditing(false);
                      setIsPopoverOpen(false);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </>
              ) : (
                isAuthenticated && (
                  <button
                    className="flex items-center gap-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditing(true);
                    }}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                )
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>


  );
};

ProfileCard.CurrentProject = CurrentProject;

ProfileCard.Skills = ({ skills }) => (
  <Stack direction="row" spacing={1} flexWrap="wrap">
    {skills.map((skill, idx) => (
      <EllipsisChip key={idx} label={skill} size="small" />
    ))}
  </Stack>
);

/////////////////////////////////////////////
// --------- Project/Resource Section ----- //
///////////////////////////////////////////// 

ProfileCard.INABProjectHeader = ({ title, img = '', imageProps, subTitle = '' }) => (
  <div className="flex flex-row items-center justify-between pb-1.5">
    <div className="w-[calc(100%-40px)]">
      <EllipsisTooltip className="font-semibold text-foreground">
        <span className="text-md font-medium">{title}</span>
      </EllipsisTooltip>
      {subTitle && (
        <EllipsisTooltip className="text-muted-foreground text-sm">
          <span>{subTitle}</span>
        </EllipsisTooltip>
      )}
    </div>
    {img && (
      <img
        src={img}
        alt="img"
        className="w-10 h-10 object-contain flex-shrink-0"
        {...imageProps}
      />
    )}
  </div>
);

ProfileCard.ProjectHeader = ({ title, status, showLeftIcon = false, showRightIcon = true, onRightIconClick, disableIcon = 'false' }) => (
  <div className="flex justify-between items-center pb-3">
    <div className="flex items-center space-x-2 max-w-[90%]">
      {showLeftIcon && (
        <div className="w-5 h-5 flex-shrink-0">
          <ProjectFile width="20px" height="20px" color="#2563eb" />
        </div>
      )}
      <EllipsisTooltip className="font-semibold text-foreground">
        <span className="text-md font-medium">{title}</span>
      </EllipsisTooltip>
    </div>
    {/* {showRightIcon && ( */}
    <EllipsisTooltip
      title={
        !disableIcon
          ? "You do not have access to the complete project and therefore cannot share it. However, you may share the workbooks you have access to."
          : ""
      }
      className={`
          flex h-4 w-4 items-center justify-center
          ${!disableIcon
          ? "cursor-not-allowed text-gray-400 hover:text-gray-500"
          : onRightIconClick
            ? "cursor-pointer hover:text-primary"
            : "cursor-default"}
        `}
      onClick={(e) => {
        e.stopPropagation()
        if (disableIcon) onRightIconClick?.()
      }}
    >
      <ExternalLink className="h-4 w-4" />
    </EllipsisTooltip>
    {/* )}  */}
    {status && (
      <EllipsisChip label={status} size="small" color={status === "Open" ? "error" : "primary"} variant="outlined" />
    )}
  </div>
);

ProfileCard.ProjectSubTitle = ({ owner }) => (
  <div className="flex items-center space-x-1">
    <BusinessIcon fontSize="small" color="action" />
    <EllipsisTooltip variant="body2" color="text.secondary" sx={{ maxWidth: 150 }}>
      <span>{owner}</span>
    </EllipsisTooltip>
  </div>
);
ProfileCard.ProjectClientName = ({ clientName }) => (
  <div className="flex items-center gap-2">
    {/* <EllipsisTooltip variant="span"  className="text-muted-foreground" >
      Client
    </EllipsisTooltip> */}
    <span className='text-muted-foreground/80 text-sm font-medium'>Client:</span>
    <EllipsisTooltip variant="span" className='font-normal text-foreground text-sm'>
      <span>{clientName}</span>
    </EllipsisTooltip>
  </div>
);

ProfileCard.ProjectDetailsSheet = ({ sheet }) => (
  <div className="flex items-center justify-between mb-[-4px]">
    <div className="flex gap-2 items-center">
      <FileText size={18} className="text-muted-foreground/80" />
      <EllipsisTooltip sx={{ maxWidth: 100, color: '#4b5563', fontSize: 14 }}>
        <span className='text-muted-foreground/80 font-medium'>Sheets</span> {'   '}
        {/* {sheet} sheets */}
      </EllipsisTooltip>
    </div>
    <EllipsisTooltip sx={{ maxWidth: 100, color: '#4b5563', fontSize: 14 }}>
      <span className="font-normal text-foreground">{sheet}</span>
    </EllipsisTooltip>

  </div>
);

ProfileCard.ProjectDetailsContent = ({
  label,
  value,
  leftIcon: Icon = <User size={18} className="text-muted-foreground/80" />,
}) => (
  <div className="flex flex-row items-center justify-between -mb-1 gap-2 ">
    <div className="flex items-center gap-2  w-[calc(50%+20px)]">
      {Icon && <div className="w-5 h-5 flex justify-center">{Icon}</div>}
      <EllipsisTooltip className="text-muted-foreground/80 font-medium text-sm">
        <span className="text-md font-medium">{label}</span>
      </EllipsisTooltip>
    </div>
    <EllipsisTooltip className="font-normal text-foreground text-sm w-[calc(50%-20px)] text-right">
      <span className="text-md font-medium">{value}</span>
    </EllipsisTooltip>
  </div>
);

ProfileCard.ProjectDetailDateContent = ({
  date,
  leftIcon: Icon = <Calendar size={18} className="text-muted-foreground/80" />,
  label,
}) => {
  const formatDate = (utcDateStr) => {
    if (!utcDateStr) return "NA";

    const parsedDate = new Date(utcDateStr);
    if (isNaN(parsedDate.getTime())) return "—";

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    }).format(parsedDate);
  };

  return (
    <div className="flex flex-col justify-between">
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2  w-[calc(50%+20px)]">
          {Icon && <div className="w-5 h-5 flex justify-center">{Icon}</div>}
          <EllipsisTooltip className="text-muted-foreground/80 font-medium text-sm text-right">
            <span className="text-md font-medium">{label}</span>
          </EllipsisTooltip>
        </div>
        {date && (
          <EllipsisTooltip className="font-normal text-foreground text-sm">
            <span className="text-md font-medium">{formatDate(date)}</span>
          </EllipsisTooltip>
        )}
      </div>
    </div>
  );
};

ProfileCard.RequestTypeChip = ({ title = 'Request Type', value }) => {
  return (
    <div className="flex items-center justify-between mt-1">
      <span className="text-muted-foreground/80 font-medium text-sm">
        {title}:
      </span>
      {/* <EllipsisChip
        variant="outlined"
        className="max-w-none"
        label={value}
        size="small"
      /> */}
      <StatusBadge variant="contain" status={value} />
    </div>
  );
};

ProfileCard.ProjectDetailsDate = ({
  created_at,
  last_sheet,
  LastSheet = false,
  createdAt = true,
}) => {
  const formatDate = (utcDateStr) => {
    if (!utcDateStr) return "NA";

    const parsedDate = new Date(utcDateStr); // Parses UTC to local time internally
    if (isNaN(parsedDate.getTime())) return "—";

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    }).format(parsedDate);
  };

  return (
    <div className="flex flex-col justify-between">
      {createdAt && (
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-muted-foreground/80" />
            <EllipsisTooltip className="text-sm text-foreground">
              <span className="text-sm text-muted-foreground/80 font-medium">
                Created
              </span>
            </EllipsisTooltip>
          </div>
          <EllipsisTooltip className="text-sm !text-foreground">
            <span>
              {formatDate(created_at)}
            </span>
          </EllipsisTooltip>
        </div>
      )}

      {LastSheet && (
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-muted-foreground/80" />
            <EllipsisTooltip className="text-sm text-gray-600">
              <span className="text-muted-foreground/80 font-medium">
                Last Sheet
              </span>
            </EllipsisTooltip>
          </div>
          <EllipsisTooltip className="text-sm text-gray-600">
            <span className="font-normal text-foreground">
              {formatDate(last_sheet)}
            </span>
          </EllipsisTooltip>
        </div>
      )}
    </div>
  );
};



ProfileCard.ProjectDetails = ({ resources, date }) => (
  <Stack direction="row" spacing={2} mt={1} mb={1} alignItems="center">
    <Stack direction="row" spacing={0.5} alignItems="center">
      <GroupsIcon fontSize="small" color="action" />
      <EllipsisTooltip variant="body2" sx={{ maxWidth: 100 }}>{resources}</EllipsisTooltip>
    </Stack>
    <Stack direction="row" spacing={0.5} alignItems="center">
      <CalendarMonthIcon fontSize="small" color="action" />
      <EllipsisTooltip variant="body2" sx={{ maxWidth: 80 }}>{date}</EllipsisTooltip>
    </Stack>
  </Stack>
);
ProfileCard.Skills = ({ skills }) => (
  <Stack direction="row" spacing={1} flexWrap="wrap">
    {skills.map((skill, idx) => (
      <EllipsisChip key={idx} label={skill} size="small" />
    ))}
  </Stack>
);

const CardChip = ({ roles, visibleCount = 2 }) => {
  //   const [showAll, setShowAll] = useState(false);

  const className = "bg-muted-foreground/10 border border-transparent dark:border-card-border text-form-label text-[12px] py-0"
  const rolesData = roles?.map((v) => `${v?.sow_role_name} (${v.allocated_resources}/${v.required_resources})`);

  return (
    <div className="flex flex-col mt-1 gap-1 justify-between">
      <div className="flex gap-2">
        {/* <div className="w-5 h-5 flex justify-center"><User size={18} className="text-muted-foreground/80" /></div> */}
        <EllipsisTooltip
          className="text-muted-foreground/80 font-medium text-sm"
        ><span>
            Requested Roles:</span>
        </EllipsisTooltip>
      </div>

      <EllipsisTooltipCount
        showDisplayCount={true}
        displayCount={rolesData.length - visibleCount}
        count={rolesData.length}
        moreButtonShowCount={visibleCount}
        className="w-fit"
        countClassName={className}
        title={
          <Chip
            list={[...rolesData].slice(0, visibleCount)}
            hideRemoveButton={true}
            className={className}
          />
        }
      >
        <Chip
          list={[...rolesData].slice(visibleCount)}
          containerClassName="flex-col"
          className="justify-between !bg-transparent border-none p-0 text-[12px] text-white dark:text-black"
          hideRemoveButton={true}
        />
      </EllipsisTooltipCount>
      {/* <Stack direction="row" flexWrap="wrap" mt={0.5} gap={1}>
        {visibleChips.map((role, idx) => (
          <EllipsisChip variant="outlined" className={className} key={idx} label={`${role.sow_role_name} (${role.allocated_resources}/${role.required_resources})`} size="small" />
        ))}

        {hiddenChips.length > 0 && !showAll && (
          <EllipsisChip
            label={`+${hiddenChips.length}`}
            size="small"
            onClick={() => setShowAll(true)}
            className={className}
            variant="outlined"
            clickable
          />
        )}

        {showAll &&
          hiddenChips.map((role, idx) => (
            <EllipsisChip
              key={visibleCount + idx}
              label={`${role.sow_role_name} (${role.allocated_resources}/${role.required_resources})`}
              variant="outlined"
              className={className}
              size="small"
            />
          ))}
      </Stack> */}
    </div>
  );
};
ProfileCard.CardChip = CardChip

ProfileCard.Team = ({ manager, lead }) => (
  <>
    <Divider />
    <Typography variant="caption" color="text.secondary" >
      TEAM
    </Typography>
    <Stack direction="row" justifyContent="space-between">
      <Stack>
        <Typography variant="caption" color="text.secondary" >
          Manager
        </Typography>
        <Typography fontWeight={500} fontSize={12} >{manager}</Typography>
      </Stack>
      <Stack>
        <Typography variant="caption" color="text.secondary" >
          Lead
        </Typography>
        <Typography fontWeight={500} fontSize={12} >{lead}</Typography>
      </Stack>
    </Stack>
  </>
);


ProfileCard.ResourcesBadge = ({ data }) => {
  return (
    // <span className="inline-block bg-primary text-primary-foreground px-2 py-[2px] rounded-full text-xs font-medium">
    //   Resources: {data}
    // </span>
    <Badge
      variant="secondary"
      // className="whitespace-nowrap text-[12px] text-ellipsis"
    >
      <EllipsisTooltip>
        Resources: {data}
      </EllipsisTooltip>
    </Badge>
  );
};
ProfileCard.InlineIconedData = ({
  children,
  leftIcon: Icon = <User size={18} className="text-muted-foreground/80" />,
  className
}) => {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div>
        {Icon && <div className="w-5 h-5 flex justify-center item-center">{Icon}</div>}
      </div>
      <EllipsisTooltip
        className="text-muted-foreground/80 font-medium text-sm"
      >
        <span> {children}</span>
      </EllipsisTooltip>
    </div>
  );
};
const getProgressColor = (status) => {
  switch (status) {
    case "Completed":
      return "rgba(161, 227, 203, 1)";
    case "Active":
      return "rgba(246, 172, 25, 1)";
    case "Pending":
      return "rgba(177, 227, 255, 1)";
    case "INAB_REJECTED":
      return "#FF5C5CCC";
    case "INAB_COMPLETED":
      return "rgba(161, 227, 203, 1)";
    case "INAB_APPROVED":
      return " rgba(54, 78, 159, 1)"
    case "INAB_IN_FLIGHT":
      return "rgba(246, 172, 25, 1)"
    case "INAB_NEW":
      return "rgba(89, 168, 212, 1)"
    default:
      return "rgba(28, 28, 28, 0.2)"; // default color
  }
};

// ProfileCard.StyledLinearProgress = styled(LinearProgress)(({ theme, value, status }) => ({
//   // Custom styles for the progress bars
//   margin: ".7rem 0",
//   width: "100%",
//   height: "6px",
//   gap: 0,
//   borderRadius: "0",
//   [`&.${linearProgressClasses.colorPrimary}`]: {
//     backgroundColor: "rgba(28, 28, 28, 0.05)",
//   },
//   [`& .${linearProgressClasses.bar}`]: {
//     borderRadius: "5px",
//     backgroundColor: getProgressColor(status), // Get color based on status
//   },
// }));
ProfileCard.StyledLinearProgress = ({ value = 0, status }) => {
  const progressColor = getProgressColor(status);
  const progressWidth = `${Math.min(Math.max(value, 0), 100)}%`;

  return (
    <div className="relative w-full h-[6px] my-[0.7rem] overflow-hidden rounded-none">
      {/* Track */}
      <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 rounded-[5px]" />

      {/* Filled bar */}
      <div
        className="absolute left-0 top-0 h-full rounded-[5px] transition-all duration-500 ease-out"
        style={{ width: progressWidth, backgroundColor: progressColor }}
      />
    </div>
  );
};

ProfileCard.Progressbar = ({ value }) => (
  <div className="flex flex-col gap-1">
    <div className="flex justify-between items-center">
      <EllipsisTooltip
        className="text-muted-foreground/80 font-medium text-sm text-right"
      >
        <span>
          Allocation Progress</span>
      </EllipsisTooltip>
      <p className="text-sm text-muted-foreground">{value}%</p>
    </div>
    <Progress value={value} className='bg-muted-foreground/10' />
  </div>
)


export default ProfileCard;
